import React, { useState, useMemo, useEffect, useCallback } from 'react'
import _ from 'lodash'
import moment from 'moment'

import { Icon } from 'pharmacy/src/display/icon'
import useAnimatedInputPlaceholderValues from 'pharmacy/src/input/useAnimatedPlaceholderValues'

import { useQuestionSearchPlaceholders } from 'mednet-cns/src/hooks/search'

import TextareaAutosize from 'react-textarea-autosize'

import { useSelector } from 'react-redux'

import classNames from 'classnames'

import css from './headerLlmSearch.scss'

const DEFAULT_INPUT_PLACEHOLDER_VALUES = [
  'What is the best strategy for treating resistant hypertension in CKD?',
  'How do you manage post-COVID pulmonary complications?',
  'What is optimal adjuvant therapy for resected high-risk melanoma?',
  'What is the best approach for managing post-stroke spasticity?',
  'What is the best treatment for hypercalcemia of malignancy?',
]

const VIEWED_PLACEHOLDER_EXAMPLE_STORAGE_KEY = 'viewed-search-header-example'
const INPUT_PLACEHOLDER_VALUES_LIMIT = 3

const openSearchPage = (value) => {
  if (!value?.trim() || typeof window === 'undefined') return
  const pathname = window.location.pathname ?? ''
  const pathnameNoIds = pathname.replace(/\/\d+/g, '/:id')
  const params = `query=${encodeURIComponent(
    value
  )}&src=popover-search&src-page=${encodeURIComponent(pathnameNoIds)}`

  window.location.assign(`/v2/search/questions?${params}`)
}

const isToday = (date) => {
  return moment(date).isSame(moment(), 'day')
}

export const HeaderLlmSearch = ({ userData }) => {
  const [specialtySuggestedPlaceholder, specialtySuggestedPlaceholderRequest] =
    useQuestionSearchPlaceholders(
      userData?.specialtyId,
      INPUT_PLACEHOLDER_VALUES_LIMIT
    )

  const [searchQuery, setSearchQuery] = useState()
  const [isMultiLine, setIsMultiLine] = useState(false)

  const highlightSearchHeader = useSelector(
    (state) => state.search.highlightSearchHeader
  )

  useEffect(() => {
    const checkWidth = () => {
      if (typeof window !== 'undefined') {
        setIsMultiLine(window.innerWidth < 544)
      }
    }

    // Initial check
    checkWidth()

    // Add resize listener
    typeof window !== 'undefined' &&
      window.addEventListener('resize', checkWidth)

    // Cleanup
    return () => {
      typeof window !== 'undefined' &&
        window.removeEventListener('resize', checkWidth)
    }
  }, [])

  const placeholderValues = useMemo(() => {
    if (!specialtySuggestedPlaceholderRequest?.isLoaded) {
      return []
    }

    if ((specialtySuggestedPlaceholder?.length ?? 0) < 3) {
      return [
        ...(specialtySuggestedPlaceholder ?? []),
        ...DEFAULT_INPUT_PLACEHOLDER_VALUES,
      ].slice(0, INPUT_PLACEHOLDER_VALUES_LIMIT)
    }

    return specialtySuggestedPlaceholder ?? []
  }, [
    specialtySuggestedPlaceholder,
    specialtySuggestedPlaceholderRequest?.isLoaded,
  ])

  const viewedSearchExampleToday = useMemo(() => {
    const viewedPlaceholderExampleAt =
      typeof window !== 'undefined'
        ? Number(
            window.localStorage.getItem(VIEWED_PLACEHOLDER_EXAMPLE_STORAGE_KEY)
          )
        : null

    return (
      viewedPlaceholderExampleAt &&
      !isNaN(viewedPlaceholderExampleAt) &&
      isToday(new Date(viewedPlaceholderExampleAt))
    )
  }, [])

  const onPlaceholderExampleChange = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(
        VIEWED_PLACEHOLDER_EXAMPLE_STORAGE_KEY,
        Date.now().toString()
      )
    }
  }, [])

  const animatedPlaceholder = useAnimatedInputPlaceholderValues({
    placeholderValues: placeholderValues ?? [],
    enableAnimation: !viewedSearchExampleToday,
    inputValue: searchQuery,
    finalPlaceholderValue: 'What clinical question do you have?',
    stopAfterSeconds: 30,
    maxValuesRounds: 1,
    onPlaceholderExampleChange,
  })

  const submitSearch = () => {
    openSearchPage(searchQuery)
  }

  const InputComponent = isMultiLine ? TextareaAutosize : 'input'

  return (
    <div className={css.searchInputOuterContainer}>
      <div className={css.searchInputContainer}>
        <Icon
          className={css.searchIcon}
          icon={['far', 'search']}
          onClick={submitSearch}
        />
        <InputComponent
          className={classNames(css.searchInput, {
            [css.highlightedSearchHeader]: highlightSearchHeader,
          })}
          onChange={(e) => setSearchQuery(e.target.value)}
          value={searchQuery}
          type="text"
          placeholder={animatedPlaceholder}
          onKeyDown={(event) => {
            event.stopPropagation()
            if (event.key === 'Enter') {
              if (!isMultiLine || !event.shiftKey) {
                event.preventDefault()
                submitSearch()
              }
            }
          }}
          {...(isMultiLine
            ? {
                maxRows: 2,
              }
            : {})}
        />
        {!_.isEmpty(searchQuery) && (
          <Icon
            className={css.clearIcon}
            icon={['far', 'times']}
            onClick={() => setSearchQuery('')}
          />
        )}
      </div>
    </div>
  )
}
